import api from './api';

export interface POSOrder {
  id: number;
  order_number: string;
  customer_id?: number;
  customer_name: string;
  customer_phone: string;
  customer_address?: string;
  prescription_number?: string;
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  payment_method: string;
  payment_status: string;
  order_status: string;
  notes?: string;
  created_by: number;
  created_at: string;
  completed_at?: string;
  updated_at: string;
}

export interface POSOrderItem {
  id: number;
  order_id: number;
  medicine_id: number;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  discount_amount: number;
  total_price: number;
  notes?: string;
  created_at: string;
}

export interface POSPayment {
  id: number;
  order_id: number;
  payment_method: string;
  amount_paid: number;
  change_amount: number;
  reference_number?: string;
  notes?: string;
  created_at: string;
}

export interface POSProduct {
  id: number;
  name: string;
  code: string;
  dosage_form: string;
  strength: string;
  unit: string;
  price: number;
  available_stock: number;
  min_stock_level: number;
  category_name?: string;
  manufacturer?: string;
  expiry_date?: string;
}

export interface POSStockLot {
  id: number;
  medicine_id: number;
  lot_number: string;
  quantity: number;
  expiry_date: string;
  purchase_price: number;
  created_at: string;
}

export interface POSOrderDetail extends POSOrder {
  items: POSOrderItem[];
  payments: POSPayment[];
  stock_out_voucher?: {
    id: number;
    voucher_number: string;
    customer_name: string;
    customer_phone: string;
    customer_address: string;
    total_amount: number;
    status: string;
    created_by_name: string;
    created_at: string;
    completed_at?: string;
  };
}

export interface POSDailyReport {
  date: string;
  total_orders: number;
  total_revenue: number;
  total_items_sold: number;
  average_order_value: number;
}

export interface POSSalesReport {
  period: string;
  total_revenue: number;
  total_orders: number;
  total_items_sold: number;
  average_order_value: number;
  top_products: Array<{
    product_name: string;
    quantity_sold: number;
    revenue: number;
  }>;
}

// Error handling utility
const handleApiError = (error: any, defaultMessage: string) => {
  console.error('API Error:', error);
  
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    
    // Ưu tiên thông báo từ API
    if (data?.message) {
      return data.message;
    }
    
    switch (status) {
      case 400:
        return data?.error || 'Dữ liệu không hợp lệ';
      case 401:
        return 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại';
      case 403:
        return 'Bạn không có quyền thực hiện thao tác này';
      case 404:
        return 'Không tìm thấy dữ liệu yêu cầu';
      case 409:
        return data?.error || 'Dữ liệu đã tồn tại';
      case 422:
        return data?.error || 'Dữ liệu không hợp lệ';
      case 500:
        return 'Lỗi máy chủ. Vui lòng thử lại sau';
      default:
        return data?.error || defaultMessage;
    }
  } else if (error.request) {
    // Network error
    return 'Không thể kết nối đến máy chủ. Vui lòng kiểm tra kết nối mạng';
  } else {
    // Other errors
    return error.message || defaultMessage;
  }
};

// Retry utility
const retryRequest = async <T>(
  requestFn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error: any) {
      lastError = error;
      
      // Don't retry on client errors (4xx)
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        throw error;
      }
      
      if (attempt < maxRetries) {
        console.warn(`Request failed, retrying... (${attempt}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
  }
  
  throw lastError;
};

// Cache utilities
const CACHE_KEY = 'pos_products_cache';
const CACHE_DURATION = 30 * 60 * 1000; // 30 phút

const getCachedProducts = (): POSProduct[] => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return [];
    
    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    // Kiểm tra cache có hết hạn chưa
    if (now - timestamp > CACHE_DURATION) {
      localStorage.removeItem(CACHE_KEY);
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('Error reading cache:', error);
    return [];
  }
};

const setCachedProducts = (products: POSProduct[]) => {
  try {
    const cacheData = {
      data: products,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error writing cache:', error);
  }
};

const clearProductsCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

export const posService = {
  // Orders
  getOrders: async (params?: any): Promise<{ data: POSOrder[]; total: number }> => {
    try {
      const response = await retryRequest(() => 
        api.get('/api/pos/orders', { params })
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error, 'Không thể tải danh sách đơn hàng'));
    }
  },

  createOrder: async (data: any): Promise<{ message: string; data: { id: number; order_number: string } }> => {
    try {
      const response = await retryRequest(() => 
        api.post('/api/pos/orders', data)
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error, 'Không thể tạo đơn hàng'));
    }
  },

  getOrderDetail: async (id: number): Promise<POSOrderDetail> => {
    try {
      const response = await retryRequest(() => 
        api.get(`/api/pos/orders/${id}`)
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error, 'Không thể tải chi tiết đơn hàng'));
    }
  },

  updateOrder: async (id: number, data: any): Promise<{ message: string }> => {
    try {
      const response = await retryRequest(() => 
        api.put(`/api/pos/orders/${id}`, data)
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error, 'Không thể cập nhật đơn hàng'));
    }
  },

  cancelOrder: async (id: number): Promise<{ message: string }> => {
    try {
      const response = await retryRequest(() => 
        api.delete(`/api/pos/orders/${id}`)
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error, 'Không thể hủy đơn hàng'));
    }
  },

  // Payments
  createPayment: async (orderId: number, data: any): Promise<{ message: string; data?: POSPayment }> => {
    try {
      const response = await retryRequest(() => 
        api.post(`/api/pos/orders/${orderId}/payments`, data)
      );
      console.log('POS payment API response:', response);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error, 'Không thể tạo thanh toán'));
    }
  },

  getPaymentHistory: async (orderId: number): Promise<POSPayment[]> => {
    try {
      const response = await retryRequest(() => 
        api.get(`/api/pos/orders/${orderId}/payments`)
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error, 'Không thể tải lịch sử thanh toán'));
    }
  },

  // Inventory
  searchProducts: async (query: string): Promise<POSProduct[]> => {
    try {
      // Nếu query rỗng, lấy từ cache trước
      if (!query || query.trim() === '') {
        const cachedProducts = getCachedProducts();
        if (cachedProducts.length > 0) {
          console.log('Using cached products:', cachedProducts.length);
          return cachedProducts;
        }
      }
      
      const response = await retryRequest(() => 
        api.get('/api/pos/inventory/search', { 
          params: { q: query, limit: 50 } 
        })
      );
      
      // Đảm bảo trả về array
      const data = response.data?.data || response.data || [];
      const products = Array.isArray(data) ? data : [];
      
      // Lưu vào cache nếu query rỗng (toàn bộ danh mục)
      if (!query || query.trim() === '') {
        setCachedProducts(products);
        console.log('Cached products:', products.length);
      }
      
      return products;
    } catch (error) {
      // Fallback to cache if API fails
      if (!query || query.trim() === '') {
        const cachedProducts = getCachedProducts();
        if (cachedProducts.length > 0) {
          console.log('API failed, using cached products:', cachedProducts.length);
          return cachedProducts;
        }
      }
      throw new Error(handleApiError(error, 'Không thể tìm kiếm sản phẩm'));
    }
  },

  // Cache management
  clearProductsCache: () => {
    clearProductsCache();
  },

  refreshProductsCache: async (): Promise<POSProduct[]> => {
    try {
      const response = await retryRequest(() => 
        api.get('/api/pos/inventory/search', { 
          params: { q: '', limit: 1000 } 
        })
      );
      
      const data = response.data?.data || response.data || [];
      const products = Array.isArray(data) ? data : [];
      
      setCachedProducts(products);
      console.log('Refreshed products cache:', products.length);
      
      return products;
    } catch (error) {
      throw new Error(handleApiError(error, 'Không thể làm mới cache sản phẩm'));
    }
  },

  getProductStock: async (medicineId: number): Promise<POSStockLot[]> => {
    try {
      const response = await retryRequest(() => 
        api.get(`/api/pos/inventory/medicine/${medicineId}/stock`)
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error, 'Không thể tải thông tin tồn kho'));
    }
  },

  // Reports
  getDailyReport: async (date: string): Promise<POSDailyReport> => {
    try {
      const response = await retryRequest(() => 
        api.get('/api/pos/reports/daily', { params: { date } })
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error, 'Không thể tải báo cáo ngày'));
    }
  },

  getSalesReport: async (startDate: string, endDate: string): Promise<POSSalesReport> => {
    try {
      const response = await retryRequest(() => 
        api.get('/api/pos/reports/sales', { 
          params: { start_date: startDate, end_date: endDate } 
        })
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error, 'Không thể tải báo cáo bán hàng'));
    }
  }
};